/**
 * Phase 2 product create: stash export blobs in IndexedDB, then upload via
 * our API (proxied to GCS) so leaving the tab mid-upload can resume.
 *
 * App Engine caps request bodies at ~32MB, so files larger than that are
 * uploaded in chunks and composed server-side.
 */

import {
  copyProductCreateFile,
  getProductCreateJob,
  uploadProductCreateFile,
} from "../api/productCreateJobs";
import { heldPrintSourceOf } from "./printFileBlobs";

const DB_NAME = "riverr.productCreateUploads";
const STORE = "blobs";
const DB_VERSION = 1;

/**
 * App Engine F2 has only 256MB RAM. Holding a large Multer buffer + Node/Firebase
 * can OOM and return a bare HTTP 500 with no JSON body. Keep chunks small.
 * Also stays well under the ~32MB request-body hard cap (multipart overhead).
 */
const UPLOAD_CHUNK_BYTES = 4 * 1024 * 1024;

// blob: URLs are tab-local print files; the server can't read them either.
const isDataUrl = (value) =>
  typeof value === "string" &&
  (value.startsWith("data:") || value.startsWith("blob:"));

/**
 * Slim the create-job JSON so the browser→API POST does not hit App Engine's
 * request size limit (axios surfaces that as a bare "Network Error").
 * Print/preview bytes stay in IndexedDB / proxied uploads — not in this payload.
 */
export function slimPayloadForCreateJob(payload) {
  const strip = (value, dropCanvasData = false) => {
    if (typeof value === "string") {
      if (isDataUrl(value)) return undefined;
      // Fabric canvasData (and similar) often embeds base64 image srcs.
      if (
        value.length > 200 &&
        (value.startsWith("{") || value.startsWith("["))
      ) {
        try {
          const cleaned = strip(JSON.parse(value), dropCanvasData);
          return JSON.stringify(cleaned ?? null);
        } catch (_) {
          return value.length > 250000 ? undefined : value;
        }
      }
      return value;
    }
    if (Array.isArray(value)) {
      return value
        .map((v) => strip(v, dropCanvasData))
        .filter((v) => v !== undefined);
    }
    if (value && typeof value === "object") {
      if (value.pending === true) return { pending: true };
      const out = {};
      Object.entries(value).forEach(([k, v]) => {
        if (dropCanvasData && k === "canvasData") return;
        if (isDataUrl(v)) return;
        const next = strip(v, dropCanvasData);
        if (next !== undefined) out[k] = next;
      });
      return out;
    }
    return value;
  };

  try {
    const clone = JSON.parse(JSON.stringify(payload || {}));
    let slim = strip(clone, false) || {};
    // Soft cap: App Engine rejects very large bodies; axios then shows
    // "Network Error" with no status. Drop canvasData if still huge.
    if (JSON.stringify(slim).length > 6 * 1024 * 1024) {
      slim = strip(clone, true) || {};
    }
    return slim;
  } catch (_) {
    return payload || {};
  }
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "jobId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IndexedDB open failed"));
  });
}

/**
 * Colors usually share one large print. Mark later files cut from the same
 * held print as `copyOf` the first, so the bytes are stored and sent once.
 */
export function markDuplicateUploads(files) {
  const firstKeyBySource = new Map();
  (files || []).forEach((f) => {
    const source = heldPrintSourceOf(f?.blob);
    if (!source || f.copyOf) return;
    const key = f.key || resolveStagedFileKey(f);
    const first = firstKeyBySource.get(source);
    if (first && first !== key) f.copyOf = first;
    else if (!first) firstKeyBySource.set(source, key);
  });
  return files;
}

export async function saveStagedBlobs(jobId, files) {
  markDuplicateUploads(files);
  const db = await openDb();
  // files: [{ key, blob, name, contentType, copyOf? }]
  const record = {
    jobId,
    createdAt: Date.now(),
    files: files.map((f) => ({
      key: f.key,
      name: f.name,
      contentType: f.contentType || "image/png",
      blob: f.copyOf ? null : f.blob,
      ...(f.copyOf ? { copyOf: f.copyOf } : {}),
    })),
  };
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadStagedBlobs(jobId) {
  const db = await openDb();
  const record = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(jobId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return record;
}

export async function clearStagedBlobs(jobId) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(jobId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (_) {
    /* ignore */
  }
}

export function makeFileKey({ role, color, placement, index, designId, size }) {
  const c = String(color || "default");
  const p = String(placement || "default");
  const d = designId ? String(designId) : "";
  const s = size ? String(size) : "";
  const designPrefix = d ? `d:${d}:` : "";
  const sizePrefix = s ? `s:${s}:` : "";
  if (role === "mockup" && index != null) {
    return `${designPrefix}${sizePrefix}mockup:${c}:${p}:${index}`;
  }
  return `${designPrefix}${sizePrefix}${role}:${c}:${p}`;
}

export function createJobErrorMessage(err, fallback) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback ||
    "Request failed"
  );
}

/** jobId → in-flight upload promise (shared by designer + ProductCreateJobWatcher). */
const uploadInFlight = new Map();

function publicUrlFromFileMeta(fileMeta) {
  if (!fileMeta || typeof fileMeta !== "object") return null;
  const url =
    fileMeta.url ||
    fileMeta.publicUrl ||
    fileMeta.storageInfo?.publicUrl ||
    null;
  return typeof url === "string" && /^https?:\/\//i.test(url) ? url : null;
}

function parseFileKey(key) {
  let parts = String(key || "").split(":");
  let designId = null;
  let size = null;
  // DSN.2 keys: d:{designId}:{role}:{color}:{placement}[:index]
  if (parts[0] === "d" && parts.length >= 3) {
    designId = parts[1] || null;
    parts = parts.slice(2);
  }
  if (parts[0] === "s" && parts.length >= 3) {
    size = parts[1] || null;
    parts = parts.slice(2);
  }
  if (parts[0] === "mockup") {
    return {
      role: "mockup",
      color: parts[1] || "default",
      placement: parts[2] || "default",
      index: parts[3] != null ? Number(parts[3]) : 0,
      designId,
      size,
    };
  }
  return {
    role: parts[0] || null,
    color: parts[1] || "default",
    placement: parts[2] || "default",
    designId,
    size,
  };
}

async function loadFilesWithRetry(jobId, memoryFiles, attempts = 8) {
  if (Array.isArray(memoryFiles) && memoryFiles.length) {
    return memoryFiles;
  }
  for (let i = 0; i < attempts; i += 1) {
    const record = await loadStagedBlobs(jobId);
    if (record?.files?.length) return record.files;
    // Watcher may race enqueue before IndexedDB put finishes.
    await new Promise((r) => setTimeout(r, 400 * (i + 1)));
  }
  return null;
}

function resolveStagedFileKey(file) {
  if (file?.key) return String(file.key);
  if (file?.role && file?.color) {
    return makeFileKey({
      role: file.role,
      color: file.color,
      placement: file.placement,
      index: file.index,
      designId: file.designId,
      size: file.size,
    });
  }
  return null;
}

async function uploadOneStagedFile(jobId, file, chunkBytes = UPLOAD_CHUNK_BYTES) {
  const blob = file.blob;
  const fileKey = resolveStagedFileKey(file);
  if (!fileKey) {
    throw new Error(
      `Missing fileKey for staged upload (${file?.role || "unknown"}/${file?.color || "?"}/${file?.placement || "?"}). Re-open the designer and save again.`
    );
  }
  if (!blob) {
    throw new Error(`Missing blob for ${fileKey}`);
  }
  const size = blob.size || 0;
  const chunkTotal = Math.max(1, Math.ceil(size / chunkBytes) || 1);
  let lastRes = null;

  for (let chunkIndex = 0; chunkIndex < chunkTotal; chunkIndex += 1) {
    const start = chunkIndex * chunkBytes;
    const end = Math.min(size, start + chunkBytes);
    const chunkBlob =
      chunkTotal === 1
        ? blob
        : blob.slice(
            start,
            end,
            file.contentType || blob.type || "image/png"
          );

    lastRes = await uploadProductCreateFile(jobId, {
      fileKey,
      blob: chunkBlob,
      name: file.name,
      contentType: file.contentType || "image/png",
      chunkIndex,
      chunkTotal,
      bytes: size,
    });
  }

  return lastRes;
}

async function uploadStagedCopy(jobId, file, files) {
  const fileKey = resolveStagedFileKey(file);
  try {
    return await copyProductCreateFile(jobId, {
      fileKey,
      copyFromFileKey: file.copyOf,
      name: file.name,
    });
  } catch (err) {
    // Older API without copy support: send the source bytes for this key.
    const source = files.find((f) => resolveStagedFileKey(f) === file.copyOf);
    if (!source?.blob) throw err;
    console.warn("[product-create] copy failed; uploading bytes", fileKey, err);
    return uploadOneStagedFile(jobId, { ...file, blob: source.blob });
  }
}

function isRetryableUploadStatus(err) {
  const status = err?.response?.status;
  return status === 413 || status === 500 || status === 502 || status === 503;
}

/** Matches the server: mockups never gate the create. */
function isOptionalStagedFile(file, role) {
  return file?.optional === true || role === "mockup";
}

/**
 * Upload pending files for a job via proxied API (not browser→GCS signed PUT).
 * Pass memoryFiles to skip waiting on IndexedDB for the initial save path.
 * Concurrent callers for the same jobId await the same promise (designer + watcher).
 */
export async function uploadStagedFilesForJob(jobId, onProgress, memoryFiles) {
  if (uploadInFlight.has(jobId)) {
    return uploadInFlight.get(jobId);
  }

  const run = (async () => {
    const files = await loadFilesWithRetry(jobId, memoryFiles);
    if (!files?.length) {
      throw new Error(
        "Upload data is no longer available in this browser. Re-open the designer and save again."
      );
    }
    // Rebuild missing keys from role/color/placement before hitting the API.
    for (const file of files) {
      if (!file.key) {
        const rebuilt = resolveStagedFileKey(file);
        if (rebuilt) file.key = rebuilt;
      }
    }
    const invalid = files.filter((f) => !resolveStagedFileKey(f));
    if (invalid.length) {
      throw new Error(
        `Design upload data is incomplete (${invalid.length} file(s) missing fileKey). Re-open the designer and save again.`
      );
    }

    // Skip files the server already finalized (resume after a mid-batch fail).
    // getProductCreateJob returns files as an array of { key, status, ... }.
    let completedOnServer = new Set();
    try {
      const jobRes = await getProductCreateJob(jobId);
      const jobFiles = jobRes?.data?.files || [];
      completedOnServer = new Set(
        (Array.isArray(jobFiles) ? jobFiles : Object.values(jobFiles))
          .filter((meta) => meta?.status === "complete" && meta?.key)
          .map((meta) => meta.key)
      );
    } catch (_) {
      /* best-effort */
    }

    let completed = completedOnServer.size;
    const total = files.length;
    let lastStatus = null;
    const completedFiles = [];

    // The server starts the create the moment the last required file lands
    // and closes uploads. Send optional mockups first so they make the listing.
    // Copies go after every byte upload so their source is already complete.
    markDuplicateUploads(files);
    const ordered = [...files].sort((a, b) => {
      const aOpt = isOptionalStagedFile(a, a.role || parseFileKey(resolveStagedFileKey(a)).role);
      const bOpt = isOptionalStagedFile(b, b.role || parseFileKey(resolveStagedFileKey(b)).role);
      return (
        Number(bOpt) - Number(aOpt) || Number(!!a.copyOf) - Number(!!b.copyOf)
      );
    });

    for (const file of ordered) {
      const fileKey = resolveStagedFileKey(file);
      const keyMeta = parseFileKey(fileKey);
      const role = file.role || keyMeta.role;
      const color = file.color || keyMeta.color;
      const placement = file.placement || keyMeta.placement;

      if (completedOnServer.has(fileKey)) {
        if (typeof onProgress === "function") {
          onProgress({
            completed,
            total,
            percent: Math.round((completed / total) * 100),
            fileKey,
            status: "uploading",
            uploadProgress: {
              completed,
              total,
              percent: Math.round((completed / total) * 100),
            },
          });
        }
        continue;
      }

      try {
        let completeRes;
        try {
          completeRes = file.copyOf
            ? await uploadStagedCopy(jobId, file, files)
            : await uploadOneStagedFile(jobId, file);
        } catch (firstErr) {
          // Bare App Engine 500s are often OOM / body-limit on a large buffer.
          // Retry once with smaller chunks before failing the job.
          if (isRetryableUploadStatus(firstErr) && (file.blob?.size || 0) > 512 * 1024) {
            completeRes = await uploadOneStagedFile(
              jobId,
              file,
              Math.floor(UPLOAD_CHUNK_BYTES / 2)
            );
          } else {
            throw firstErr;
          }
        }
        lastStatus = completeRes?.data || completeRes;
        const url = publicUrlFromFileMeta(lastStatus?.fileMeta);
        if (url) {
          completedFiles.push({
            key: fileKey,
            role,
            color,
            placement,
            url,
            fileMeta: lastStatus.fileMeta,
          });
        }
      } catch (err) {
        const status = err?.response?.status;
        if (isOptionalStagedFile(file, role)) {
          console.warn("[product-create] optional upload skipped", fileKey, err);
          continue;
        }
        const detail = createJobErrorMessage(
          err,
          `Failed to upload ${file.name || fileKey}`
        );
        const message =
          status === 500 || status === 413
            ? `${detail} (upload HTTP ${status}; file may be too large for the API. Try again)`
            : detail;
        const wrapped = new Error(message);
        wrapped.response = err?.response;
        throw wrapped;
      }

      completed += 1;
      if (typeof onProgress === "function") {
        onProgress({
          completed,
          total,
          percent: Math.round((completed / total) * 100),
          fileKey,
          status: lastStatus?.status,
          stage: lastStatus?.stage,
          uploadProgress: lastStatus?.uploadProgress || {
            completed,
            total,
            percent: Math.round((completed / total) * 100),
          },
        });
      }
    }

    await clearStagedBlobs(jobId);
    return {
      ...(lastStatus && typeof lastStatus === "object" ? lastStatus : {}),
      completedFiles,
    };
  })();

  uploadInFlight.set(jobId, run);
  try {
    return await run;
  } finally {
    uploadInFlight.delete(jobId);
  }
}
